import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from "react-redux";
import useUserAccess from "../../../../Hooks/useUserAccess";
import UserSearchNew from "../../../Search/UserSearchNew";
import { UserRoles } from "../../../../Constants/userRoles";
import { Checkbox } from "../../../FormComponents/Checkbox";
import { RadioButtons } from "../../../FormComponents/RadioButtons";
import { FormInput } from "../../../FormComponents/Input/Input";
import { vossSubscriptionItemTerminationTiming, VossSubscriptionItemTerminationTimingText } from "../../../../Constants/VOSS/vossSubscriptionItemTerminationTiming";
import { VossCreditKind, VossCreditKindText } from "../../../../Constants/VOSS/vossCreditKind";
import { VossCreditBehaviour, VossCreditBehaviourText } from "../../../../Constants/VOSS/vossCreditBehaviour";
import helpers from "../../../../Utils/helpers";
import Tooltip, { TooltipContent, TooltipTrigger } from "../../../Overlays/Tooltip";
import SkynetIcon from "../../../Fundamentals/SkynetIcon";
import { Dropdown } from "../../../FormComponents/Dropdown";
import SkynetForm from '../../../Fundamentals/Form/SkynetForm';
import { TerminationConfigs, TerminationTypes } from '../../../../Constants/VOSS/Termination/TerminationConfigs'
import { httpFetch } from "../../../../Utils/httpUtils";
import subscriptionUtils from "../../../../Utils/subscriptionUtils";
import { RankDirection } from "../../../../Containers/Customer/VOSS/Subscriptions/SubscriptionMainInfo";

const SubscriptionTerminationForm = ({ form: { data }, subscription, onChange, warrantyEligibility }) => {
    const { t } = useTranslation();
    const { hasAdminAccess, hasReadWriteAccess } = useUserAccess();
    const dispatch = useDispatch();

    const [orderType, setOrderType] = useState(data.orderType || 'ordinary');

    const orderTypeOptions = [
        { id: 'ordinary', label: t('confirmordermodal.ordinary') },
        { id: 'other', label: t('confirmordermodal.orderforother'), disabled: !hasReadWriteAccess(UserRoles.OrderForOther) },
        { id: 'partner', label: t('confirmordermodal.partner') },
        { id: 'web', label: t('confirmordermodal.web') },
        { id: 'onlineSales', label: t('confirmordermodal.onlineorder') }
    ];

    useEffect(() => {
        if (orderType !== 'other' && data.salesPersonEmail) {
            onChange('salesPersonEmail', null);
        }
    }, [orderType, data.salesPersonEmail, onChange]);

    useEffect(() => {
        if (data.orderType !== orderType) {
            onChange('orderType', orderType);
        }
    }, [orderType, data.orderType, onChange]);

    const reasons = useSelector((state) => state.CommonReducer.dropdownlists?.['SubscriptionTerminationReasons']) || [];
    const subscriptions = useSelector((state) => state.CustomerReducer.subscriptions);
    const selectedReason = useMemo(() => reasons.find(r => r.id === data.reason), [reasons, data.reason]);

    const [parentSub, setParentSub] = useState(null);
    const [hasParentDep, setHasParentDep] = useState(false);
    useEffect(() => {
        const lowerRankDep = subscriptionUtils.findDependentByRank(subscriptions, subscription, RankDirection.LOWER);
        setHasParentDep(!!lowerRankDep);
        if (lowerRankDep?.id) {
            httpFetch(`/api/subscriptions/${lowerRankDep.id}`, dispatch)
                .then(setParentSub)
                .catch(() => setParentSub(null));
        } else {
            setParentSub(null);
        }
    }, [subscriptions, subscription?.id, dispatch]);

    const onReasonChange = (value) => {
        onChange('subReason', null)
        onChange("reason", value)
    }

    const onTerminationTimingChange = (value) => {
        const config = TerminationConfigs[value];

        Object.entries(config).forEach(([key, val]) => {
            onChange(key, val);
        });
    }

    const creditDisabled = data.terminationType != TerminationTypes.Immediately;
    const isWarrantyChecked = data.useWarranty;
    const parentHasScheduledChange = parentSub?.scheduledChanges?.length > 0;
    const isLoadingParent = hasParentDep && !parentSub;

    const hasEvergreenBindingPeriod = subscriptionUtils.hasEvergreenBindingPeriod(subscription);

    const timingOptions = [
        { id: 'termination-timing-1', label: hasEvergreenBindingPeriod ? t('subscriptiontermination.endOfNextMonthTermination') : `${t(VossSubscriptionItemTerminationTimingText[vossSubscriptionItemTerminationTiming.AtBindingPeriodEnd])} (${helpers.formatDate(subscription.periodEnd)})`, value: TerminationTypes.CurrentPeriodEnd },
        ...(hasEvergreenBindingPeriod ? [] : [{ id: 'termination-timing-2', label: `${t("subscriptiontermination.afterNextPeriodTermination")}`, value: TerminationTypes.NextPeriodEnd }]),
        ...(hasAdminAccess(UserRoles.SubscriptionAdmin) ? [{ id: 'termination-timing-3', label: t(VossSubscriptionItemTerminationTimingText[vossSubscriptionItemTerminationTiming.Immediately]), value: TerminationTypes.Immediately, disabled: isLoadingParent || parentHasScheduledChange }] : [])
    ];

    const creditKindOptions = [
        { id: 'credit-kind-1', label: t(VossCreditKindText[VossCreditKind.Normal]), value: VossCreditKind.Normal },
        { id: 'credit-kind-2', label: t(VossCreditKindText[VossCreditKind.Internal]), value: VossCreditKind.Internal }
    ]

    const creditBehaviourOptions = [
        { id: 'credit-behaviour-1', label: t(VossCreditBehaviourText[VossCreditBehaviour.Full]), value: VossCreditBehaviour.Full },
        { id: 'credit-behaviour-2', label: t(VossCreditBehaviourText[VossCreditBehaviour.Prorate]), value: VossCreditBehaviour.Prorate },
        { id: 'credit-behaviour-3', label: t(VossCreditBehaviourText[VossCreditBehaviour.None]), value: VossCreditBehaviour.None }
    ]

    return <SkynetForm>
        <fieldset className="d-grid col-12 gap-12">
            <Dropdown
                label={t('confirmordermodal.orderType')}
                options={orderTypeOptions}
                value={orderType}
                onChange={setOrderType}
                property="label"
                disabledOptionKey="disabled"
                required
            />
            {hasReadWriteAccess(UserRoles.OrderForOther) && orderType === 'other' && (
                <UserSearchNew
                    label={t('confirmordermodal.orderforother')}
                    onChange={(user) => onChange('salesPersonEmail', user?.email || null)}
                    tooltip={t('confirmordermodal.orderforothertooltip')}
                    value={data.salesPersonEmail}
                />
            )}
        </fieldset>
        {warrantyEligibility && <fieldset className="d-grid col-12 gap-12">
            <div className="form-group mb-0 required d-flex flex-row gap-12 align-items-center">
                <div className='d-inline'>
                    <Checkbox label={t("subscriptiontermination.30daywarranty")} checked={isWarrantyChecked} onChange={(value) => { onChange('useWarranty', value) }} />
                </div>
                <Tooltip placement='right' arrowPlacement='right in'>
                    <TooltipTrigger className="d-flex">
                        {<SkynetIcon icon={'vismaicon-info vismaicon-sm'} />}
                    </TooltipTrigger>
                    <TooltipContent>
                        {t("subscriptiontermination.warrantytooltip")}
                    </TooltipContent>
                </Tooltip>
            </div>
        </fieldset>}
        {!isWarrantyChecked && <>
            <fieldset className="d-grid col-12 gap-12">
                <div className="form-group mb-0 required d-flex flex-column align-items-start">
                    <RadioButtons label={t("subscriptiontermination.timeoftermination")} items={timingOptions} value={data.terminationType} onChange={onTerminationTimingChange} />
                </div>
            </fieldset>
            {!creditDisabled && <fieldset className={`d-grid col-12 gap-12`}>
                <label className='bold'>{t("subscriptiontermination.credit")}</label>
                <div className="form-group mb-0 required d-flex flex-column align-items-start">
                    <RadioButtons label={t("subscriptiontermination.creditkind")} items={creditKindOptions} value={data.creditKind} onChange={(value) => onChange("creditKind", value)} />
                </div>
                <div className="form-group mb-0 required d-flex flex-column align-items-start">
                    <RadioButtons label={t("subscriptiontermination.creditbehaviour")} items={creditBehaviourOptions} value={data.behaviour} onChange={(value) => onChange("behaviour", value)} />
                </div>
                <FormInput
                    label={t("subscriptiontermination.invoicetext")}
                    placeholder={t("subscriptiontermination.textshownoninvoice")}
                    value={data.invoiceLineTexts}
                    onChange={(input) => onChange("invoiceLineTexts", input)} />
            </fieldset>}
            <fieldset className="d-grid col-12 gap-12">
                <label className='bold'>{t("subscriptiontermination.reason")}</label>
                <Dropdown required disabled={isWarrantyChecked} options={reasons} placeholder={t("subscriptiontermination.reasondropdown")} value={data.reason} onChange={onReasonChange} label={t("subscriptiontermination.terminationreason")} />
                {selectedReason?.subReasons?.length > 0 &&
                    <Dropdown
                        options={selectedReason.subReasons}
                        placeholder={t("subscriptiontermination.reasondropdown")}
                        onChange={(value) => onChange('subReason', value)}
                        allowClear={true}
                        value={data.subReason}
                        label={t("subscriptiontermination.subterminationreason")} />
                }
            </fieldset>
        </>}
        <fieldset className="d-grid col-12 gap-12">
            <FormInput
                label={t("common.comment")}
                placeholder={t("common.commentplaceholder")}
                value={data.comment}
                readOnly={false}
                onChange={(input) => onChange("comment", input)}
            />
        </fieldset>
    </SkynetForm>
}

export default SubscriptionTerminationForm;